import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../lib/api';

// ─── Types ───────────────────────────────────────────────────────────────────

interface ChecklistItem {
  id: string;
  text: string;
  completed: boolean;
  completedBy?: { id: string; name: string } | null;
  completedAt?: string | null;
  sortOrder: number;
}

interface Checklist {
  id: string;
  familyId: string;
  title: string;
  category: string;
  dependent?: { id: string; name: string } | null;
  items: ChecklistItem[];
}

const CATEGORY_COLOUR: Record<string, string> = {
  HOME_SAFETY: 'bg-blue-100 text-blue-800',
  HOSPITAL_DISCHARGE: 'bg-purple-100 text-purple-800',
  EMERGENCY_PLAN: 'bg-red-100 text-red-800',
  MEDICATION_REVIEW: 'bg-teal-100 text-teal-800',
};

const CATEGORY_LABEL: Record<string, string> = {
  HOME_SAFETY: 'Home Safety',
  HOSPITAL_DISCHARGE: 'Hospital Discharge',
  EMERGENCY_PLAN: 'Emergency Plan',
  MEDICATION_REVIEW: 'Medication Review',
};

// ─── ChecklistDetailPage ──────────────────────────────────────────────────────

export function ChecklistDetailPage() {
  const { checklistId } = useParams<{ checklistId: string }>();
  const navigate = useNavigate();

  const [checklist, setChecklist] = useState<Checklist | null>(null);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState<string | null>(null);
  const [familyId, setFamilyId] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const families = await api.getFamilies();
        if (!families?.length) { navigate('/onboarding'); return; }
        const fid = families[0].id;
        setFamilyId(fid);

        if (!checklistId) return;
        const list = await api.getChecklist(fid, checklistId);
        setChecklist(list as Checklist);
      } catch {
        navigate('/checklists');
      } finally {
        setLoading(false);
      }
    })();
  }, [checklistId, navigate]);

  const handleToggle = async (itemId: string) => {
    if (!checklist || !familyId || toggling) return;
    setToggling(itemId);

    // Optimistic update
    setChecklist((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        items: prev.items.map((item) =>
          item.id === itemId ? { ...item, completed: !item.completed } : item
        ),
      };
    });

    try {
      const updated = await api.toggleChecklistItem(familyId, checklist.id, itemId);
      setChecklist((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          items: prev.items.map((item) =>
            item.id === itemId ? (updated as ChecklistItem) : item
          ),
        };
      });
    } catch {
      // Revert on error
      setChecklist((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          items: prev.items.map((item) =>
            item.id === itemId ? { ...item, completed: !item.completed } : item
          ),
        };
      });
    } finally {
      setToggling(null);
    }
  };

  const formatDate = (iso?: string | null) => {
    if (!iso) return '';
    return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full text-gray-500 text-sm">
        Loading…
      </div>
    );
  }

  if (!checklist) return null;

  const completedCount = checklist.items.filter((i) => i.completed).length;
  const totalCount = checklist.items.length;
  const pct = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;
  const allDone = completedCount === totalCount && totalCount > 0;

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Back */}
      <button
        onClick={() => navigate('/checklists')}
        className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-900 mb-6"
      >
        ← Checklists
      </button>

      {/* Header */}
      <div className="mb-6">
        <div className="flex items-start justify-between gap-3 mb-1">
          <h1 className="text-xl font-semibold text-gray-900">{checklist.title}</h1>
          <span className={`shrink-0 text-[10px] font-semibold px-2 py-0.5 rounded-full ${CATEGORY_COLOUR[checklist.category] ?? 'bg-gray-100 text-gray-600'}`}>
            {CATEGORY_LABEL[checklist.category] ?? checklist.category}
          </span>
        </div>
        {checklist.dependent && (
          <p className="text-sm text-gray-500">For {checklist.dependent.name}</p>
        )}
      </div>

      {/* Progress */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-sm text-gray-600">
            {allDone ? 'All done!' : `${completedCount} of ${totalCount} complete`}
          </span>
          <span className="text-sm font-medium text-gray-700">{pct}%</span>
        </div>
        <div className="w-full bg-gray-100 rounded-full h-2">
          <div
            className={`h-2 rounded-full transition-all duration-300 ${allDone ? 'bg-green-500' : 'bg-primary-500'}`}
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>

      {/* Items */}
      <div className="bg-white rounded-xl border border-gray-200 divide-y divide-gray-100">
        {checklist.items.map((item) => (
          <div
            key={item.id}
            className={`flex items-start gap-3 px-4 py-3.5 ${toggling === item.id ? 'opacity-60' : ''}`}
          >
            <button
              onClick={() => handleToggle(item.id)}
              disabled={toggling !== null}
              className={`mt-0.5 w-5 h-5 shrink-0 rounded border-2 flex items-center justify-center transition-colors ${
                item.completed
                  ? 'bg-primary-600 border-primary-600'
                  : 'border-gray-300 hover:border-primary-400 bg-white'
              }`}
              aria-label={item.completed ? 'Mark incomplete' : 'Mark complete'}
            >
              {item.completed && (
                <svg className="w-3 h-3 text-white" viewBox="0 0 12 12" fill="none">
                  <path d="M2 6l3 3 5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              )}
            </button>

            <div className="flex-1 min-w-0">
              <p className={`text-sm ${item.completed ? 'line-through text-gray-400' : 'text-gray-900'}`}>
                {item.text}
              </p>
              {item.completed && item.completedBy && (
                <p className="text-xs text-gray-400 mt-0.5">
                  {item.completedBy.name}
                  {item.completedAt && ` · ${formatDate(item.completedAt)}`}
                </p>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
